import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ChunkyButton, Confetti, StarRow, ShakeWidget, FloatingXpBadge } from '../../components/GameUI';

// Word-guessing game — port of the mobile app's lib/games/hangman/hangman_screen.dart.
// Plays through up to 5 rounds (one word per round) drawn from the words the
// hub page passed in; each round starts with 6 lives, shared between wrong
// letter guesses and hints (a hint costs a life, same as a miss, so there's
// only one resource to reason about — same design as the mobile source).
//
// Presentation deviates from the mobile source in one place: the mobile app
// reacts with an emoji "buddy" (😊 → 💀) instead of drawing an actual
// hangman, since it wanted to avoid a literal gallows in a language-learning
// app. This port draws a simple line-art SVG figure instead (per this port's
// brief), revealed stroke-by-stroke as lives are spent — same underlying
// resource (lives lost), just a different visual. All scoring, lives, and
// round-advance mechanics are otherwise a straight port.

const START_LIVES = 6;
const TOTAL_ROUNDS = 5;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Six progressively-revealed limbs, drawn onto a static gallows. Matches
// START_LIVES so the figure is "complete" exactly when lives hit 0.
const FIGURE_PARTS = [
  <circle key="head" cx="90" cy="35" r="10" />,
  <line key="torso" x1="90" y1="45" x2="90" y2="85" />,
  <line key="armL" x1="90" y1="55" x2="72" y2="68" />,
  <line key="armR" x1="90" y1="55" x2="108" y2="68" />,
  <line key="legL" x1="90" y1="85" x2="75" y2="112" />,
  <line key="legR" x1="90" y1="85" x2="105" y2="112" />,
];

function HangmanFigure({ wrongCount }) {
  const revealed = Math.max(0, Math.min(FIGURE_PARTS.length, wrongCount));
  return (
    <svg
      viewBox="0 0 130 140"
      width="140"
      height="150"
      className="mx-auto"
      fill="none"
      stroke="#94A3B8"
      strokeWidth="4"
      strokeLinecap="round"
    >
      {/* gallows (static) */}
      <line x1="10" y1="130" x2="70" y2="130" />
      <line x1="30" y1="130" x2="30" y2="12" />
      <line x1="30" y1="12" x2="90" y2="12" />
      <line x1="90" y1="12" x2="90" y2="25" />
      {/* figure, revealed one limb per life lost */}
      <g stroke="#FF4B4B">{FIGURE_PARTS.slice(0, revealed)}</g>
    </svg>
  );
}

export default function HangmanGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { words = [], targetLang = { code: '', label: '', dir: 'ltr' }, langPair = 'guest' } = state || {};

  // Pool of words for this session (reshuffled on mount and on every
  // "Try Again") and the dynamic on-screen keyboard — built from every
  // letter appearing across the whole word pool, not a fixed Latin layout,
  // so it generalizes to non-Latin target-language scripts the same way
  // the mobile source does.
  function freshRoundWords() {
    const pool = shuffle(words);
    return pool.slice(0, Math.min(TOTAL_ROUNDS, pool.length));
  }

  const [roundWords, setRoundWords] = useState(freshRoundWords);

  const alphabet = useMemo(() => {
    const letters = new Set();
    words.forEach((w) => Array.from(String(w).toUpperCase()).forEach((ch) => letters.add(ch)));
    return Array.from(letters).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalid = !roundWords.length;

  const [round, setRound] = useState(0);
  const [currentWord, setCurrentWord] = useState(() => String(roundWords[0] || '').toUpperCase());
  const [guessed, setGuessed] = useState(() => new Set());
  const [wrongGuesses, setWrongGuesses] = useState(() => new Set());
  const [lives, setLives] = useState(START_LIVES);
  const [roundLocked, setRoundLocked] = useState(false);
  const [score, setScore] = useState(0);
  const [perfectSession, setPerfectSession] = useState(true);
  const [shakeCount, setShakeCount] = useState(0);
  const [done, setDone] = useState(false);

  const rewardedRef = useRef(false);

  useEffect(() => {
    if (invalid) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Re)start a round whenever `round` advances — resets the guessed/wrong
  // letter sets, lives, and lock for the new word.
  useEffect(() => {
    if (invalid) return;
    setCurrentWord(String(roundWords[round] || '').toUpperCase());
    setGuessed(new Set());
    setWrongGuesses(new Set());
    setLives(START_LIVES);
    setRoundLocked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  function isSolved(guessedSet) {
    return Array.from(currentWord).every((ch) => guessedSet.has(ch));
  }

  function advance() {
    if (round + 1 >= roundWords.length) {
      finish();
    } else {
      setRound(round + 1);
    }
  }

  function roundWon() {
    setRoundLocked(true);
    setScore((s) => s + 1);
    LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `hangman:${currentWord}` });
    setTimeout(advance, 700);
  }

  function roundFailed() {
    setRoundLocked(true);
    SoundEffectService.playWrong();
    setGuessed((prev) => {
      const next = new Set(prev);
      Array.from(currentWord).forEach((ch) => next.add(ch));
      return next;
    });
    setTimeout(advance, 1400);
  }

  function finish() {
    setDone(true);
  }

  // Fires once `done` commits, by which point `score`/`perfectSession` both
  // hold their final values (same reward-on-completion pattern as the other
  // ported games, e.g. WordMatchGame's `useEffect` keyed on `done`) — avoids
  // needing shadow refs just to dodge a stale closure in the round-advance
  // `setTimeout`.
  useEffect(() => {
    if (!done || rewardedRef.current) return;
    rewardedRef.current = true;
    const flawlessNow = perfectSession && score === roundWords.length;
    const xp = score * 6 + (flawlessNow ? 10 : 0);
    StreakService.recordActivity();
    XpService.addXp(xp);
    if (flawlessNow) SoundEffectService.playCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  function restart() {
    const fresh = freshRoundWords();
    setRoundWords(fresh);
    setRound(0);
    setCurrentWord(String(fresh[0] || '').toUpperCase());
    setGuessed(new Set());
    setWrongGuesses(new Set());
    setLives(START_LIVES);
    setRoundLocked(false);
    setScore(0);
    setPerfectSession(true);
    setShakeCount(0);
    setDone(false);
    rewardedRef.current = false;
  }

  function guessLetter(letter) {
    if (roundLocked || guessed.has(letter)) return;
    const nextGuessed = new Set(guessed);
    nextGuessed.add(letter);
    setGuessed(nextGuessed);

    if (currentWord.includes(letter)) {
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      if (isSolved(nextGuessed)) roundWon();
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
      setWrongGuesses((prev) => new Set(prev).add(letter));
      setPerfectSession(false);
      setShakeCount((c) => c + 1);
      const nextLives = lives - 1;
      setLives(nextLives);
      if (nextLives <= 0) roundFailed();
    }
  }

  function useHint() {
    if (roundLocked) return;
    const remaining = Array.from(new Set(Array.from(currentWord).filter((ch) => !guessed.has(ch))));
    if (!remaining.length) return;
    const letter = remaining[Math.floor(Math.random() * remaining.length)];
    const nextGuessed = new Set(guessed);
    nextGuessed.add(letter);
    setGuessed(nextGuessed);
    setPerfectSession(false);
    const nextLives = lives - 1;
    setLives(nextLives);
    if (isSolved(nextGuessed)) {
      roundWon();
    } else if (nextLives <= 0) {
      roundFailed();
    }
  }

  const flawless = perfectSession && score === roundWords.length;
  const percent = roundWords.length ? Math.round((score / roundWords.length) * 100) : 0;
  const stars = ExerciseScoreService.starsForPercent(percent);
  const xpEarned = score * 6 + (flawless ? 10 : 0);
  const wrongCount = START_LIVES - lives;

  if (invalid) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
          >
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.hangman')}</h1>
          {!done ? (
            <button
              onClick={useHint}
              disabled={roundLocked}
              className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 shadow-sm hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              💡 {t('games.hangmanHint', 'Hint')}
            </button>
          ) : (
            <span className="w-16" />
          )}
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {flawless && <Confetti />}
            <span className="text-6xl">{flawless ? '🏆' : score > 0 ? '🎉' : '💪'}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.hangmanComplete', 'Nice guessing!')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('games.hangmanScoreLine', '{{score}}/{{total}} words solved', {
                score,
                total: roundWords.length,
              })}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton
                color="#4F46E5"
                onClick={restart}
                className="w-full py-4 text-center text-white"
              >
                {t('games.tryAgain')}
              </ChunkyButton>
              <button
                onClick={() => navigate('/games')}
                className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-base font-black text-slate-900">
                {round + 1}/{roundWords.length}
              </span>
              <div className="flex items-center gap-1">
                {Array.from({ length: START_LIVES }).map((_, i) => (
                  <span key={i} style={{ color: i < lives ? '#FF4B4B' : '#E2E8F0', fontSize: 16, lineHeight: 1 }}>
                    ♥
                  </span>
                ))}
              </div>
            </div>

            <ShakeWidget trigger={shakeCount}>
              <HangmanFigure wrongCount={wrongCount} />
            </ShakeWidget>

            <div dir={targetLang.dir} className="mt-6 flex flex-wrap items-end justify-center gap-1.5">
              {Array.from(currentWord).map((letter, i) => {
                const revealed = guessed.has(letter);
                return (
                  <div
                    key={i}
                    className="flex h-10 w-8 items-center justify-center text-xl font-black"
                    style={{ borderBottom: `3px solid ${revealed ? '#4F46E5' : '#E2E8F0'}`, color: '#0F172A' }}
                  >
                    {revealed ? letter : ''}
                  </div>
                );
              })}
            </div>

            <div dir={targetLang.dir} className="mt-7 flex flex-wrap justify-center gap-2">
              {alphabet.map((letter) => {
                const isGuessed = guessed.has(letter);
                const isWrong = wrongGuesses.has(letter);
                let bg = '#F1F5F9';
                let fg = '#0F172A';
                let border = '#E2E8F0';
                if (isGuessed) {
                  if (isWrong) {
                    bg = '#FFDFE0';
                    fg = '#8B0000';
                    border = '#FF4B4B';
                  } else {
                    bg = '#D7FFB8';
                    fg = '#2B6B00';
                    border = '#58CC02';
                  }
                }
                return (
                  <button
                    key={letter}
                    type="button"
                    disabled={isGuessed || roundLocked}
                    onClick={() => guessLetter(letter)}
                    className="flex h-10 w-9 items-center justify-center rounded-xl border-2 text-sm font-extrabold transition-colors disabled:cursor-not-allowed"
                    style={{ background: bg, color: fg, borderColor: border }}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
